import { useQuery } from "@tanstack/react-query";
import { eventsApi } from "@/lib/humhub-api/events";
import { getApiErrorStatus } from "@/lib/apiErrors";
import type { LinkMetadata } from "@/lib/humhub-api/linkPreview";
import { linkPreviewApi } from "@/lib/humhub-api/linkPreview";
import { extractEventCode } from "@/lib/utils/linkUtils";

type UseLinkPreviewOptions = {
  enabled?: boolean;
};

type LinkPreviewQueryResult =
  | {
      metadata: LinkMetadata;
      errorStatus: null;
      failed: false;
    }
  | {
      metadata: null;
      errorStatus: number | null;
      failed: true;
    };

/**
 * リンクプレビューのメタデータを取得するカスタムフック
 * @param url プレビューを取得するURL
 * @returns リンクプレビューの状態と結果
 */
export const useLinkPreview = (
  url: string,
  { enabled = true }: UseLinkPreviewOptions = {},
) => {
  const eventCode = url ? extractEventCode(url) : null;

  const {
    data: previewResult,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery<LinkPreviewQueryResult>({
    queryKey: ["linkPreview", url],
    queryFn: async () => {
      try {
        // 内部イベントリンクかどうかをチェック
        if (eventCode) {
          // イベントAPIを使用してデータを取得
          const eventData = await eventsApi.getPublicEventByCode(eventCode);

          // イベントデータをLinkMetadata形式に変換
          const linkMetadata: LinkMetadata = {
            title: eventData.title,
            description:
              eventData.description || eventData.container?.description,
            image: eventData.content?.files?.[0]?.url,
            siteName: eventData.container?.name,
            isEvent: true,
            eventData: eventData,
          };

          return {
            metadata: linkMetadata,
            errorStatus: null,
            failed: false,
          };
        }

        // 通常のリンクプレビューAPIを使用
        const data = await linkPreviewApi.getLinkPreview(url);

        if (data.error) {
          throw new Error(data.error);
        }

        return {
          metadata: data,
          errorStatus: null,
          failed: false,
        };
      } catch (err) {
        const status = getApiErrorStatus(err);
        if (status === 404) {
          return {
            metadata: null,
            errorStatus: status,
            failed: true,
          };
        }

        console.error("Failed to fetch link metadata:", err);
        throw err;
      }
    },
    enabled: enabled && !!url, // URLがある場合のみクエリ実行
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // リンクプレビューは5分間キャッシュ
    gcTime: 30 * 60 * 1000, // 30分間メモリに保持
    meta: {
      isInternalEvent: !!eventCode,
      eventCode: eventCode ?? undefined,
    },
  });

  return {
    metadata: previewResult?.metadata ?? null,
    isLoading,
    error: Boolean(previewResult?.failed) || Boolean(queryError),
    errorStatus: previewResult?.errorStatus ?? getApiErrorStatus(queryError),
    refetch,
  };
};
